import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormHelperText, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import TextBoxWithTooltip from '../../textBoxWithTooltip';
import CustomCheckbox from '../../customCheckbox';
import {
  USER_BASIC_PERSONAL_INFORMATION,
  USER_EMPLOYMENT_INFORMATION,
} from '../../../../constants/people';

const ALWAYS_VISIBLE_FIELDS = new Set([
  USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME,
  USER_BASIC_PERSONAL_INFORMATION.LAST_NAME,
  USER_BASIC_PERSONAL_INFORMATION.EMAIL,
  USER_BASIC_PERSONAL_INFORMATION.GENDER,
  USER_BASIC_PERSONAL_INFORMATION.DATE_OF_BIRTH,
  USER_BASIC_PERSONAL_INFORMATION.LANGUAGE,
  USER_BASIC_PERSONAL_INFORMATION.TIME_ZONE,
  USER_EMPLOYMENT_INFORMATION.JOB_TITLE,
  USER_EMPLOYMENT_INFORMATION.REPORT_TO,
  USER_EMPLOYMENT_INFORMATION.DIVISION,
  USER_EMPLOYMENT_INFORMATION.DEPARTMENT,
  USER_EMPLOYMENT_INFORMATION.FRAMEWORK,
  USER_EMPLOYMENT_INFORMATION.TRACK,
  USER_EMPLOYMENT_INFORMATION.LEVEL,
  USER_EMPLOYMENT_INFORMATION.EMPLOYMENT_DATE,
  USER_EMPLOYMENT_INFORMATION.EMPLOYMENT_TERMINATION_DATE,
]);

const ALWAYS_NON_SENSITIVE_FIELDS = new Set([
  USER_BASIC_PERSONAL_INFORMATION.FIRST_NAME,
  USER_BASIC_PERSONAL_INFORMATION.LAST_NAME,
  USER_EMPLOYMENT_INFORMATION.JOB_TITLE,
  USER_BASIC_PERSONAL_INFORMATION.EMAIL,
  USER_EMPLOYMENT_INFORMATION.FRAMEWORK,
  USER_EMPLOYMENT_INFORMATION.LEVEL,
  USER_EMPLOYMENT_INFORMATION.TRACK,
]);

const SENSITIVE_LINKED_FIELDS = {
  [USER_EMPLOYMENT_INFORMATION.DIVISION]:
    USER_EMPLOYMENT_INFORMATION.DEPARTMENT,
  [USER_EMPLOYMENT_INFORMATION.DEPARTMENT]:
    USER_EMPLOYMENT_INFORMATION.DIVISION,
};

const useStyles = makeStyles(({ spacing }) => ({
  root: {
    marginBottom: spacing(1),
  },
  header: {
    display: 'grid',
    gridTemplateColumns: 'minMax(0, 357px) auto auto',
    gridColumnGap: spacing(2),
  },
  checkboxLabel: {
    justifyContent: 'center',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'minMax(0, 350px) auto auto',
    gridColumnGap: spacing(2),
    alignItems: 'center',
  },
  field: {
    position: 'relative',
    marginBottom: spacing(2),
  },
  checkbox: {
    marginBottom: spacing(2),
  },
  checkboxWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    display: 'flex',
  },
  disabledCheckboxWrapper: {
    cursor: 'default',
  },
  checkboxRoot: {
    marginTop: spacing(1),
    marginLeft: 0,
    marginRight: 0,
  },
  labelWrapper: {
    width: 350,
  },
}));

const PeopleInfoField = ({
  id,
  value,
  fieldTypes,
  onChange,
  headersTranslations,
}) => {
  const classes = useStyles();
  const [, visibleCheckbox, sensitiveCheckbox] = fieldTypes;
  const isVisibleCheckboxDisabled = ALWAYS_VISIBLE_FIELDS.has(value?.name);
  const isSensitiveCheckboxDisabled = ALWAYS_NON_SENSITIVE_FIELDS.has(
    value?.name
  );

  const handleChangeCheckbox = useCallback(
    (isChecked, name) => {
      const linkedFieldName =
        name === sensitiveCheckbox.name && SENSITIVE_LINKED_FIELDS[value?.name];

      const linkedFieldUpdates = linkedFieldName
        ? {
            [linkedFieldName]: {
              [sensitiveCheckbox.name]: isChecked,
            },
          }
        : undefined;

      onChange({ ...value, [name]: isChecked }, id, linkedFieldUpdates);
    },
    [id, value, onChange, sensitiveCheckbox.name]
  );

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div />
        <FormHelperText
          classes={{
            root: classes.checkboxLabel,
          }}
        >
          {headersTranslations.visibleLabel}
        </FormHelperText>
        <FormHelperText
          classes={{
            root: classes.checkboxLabel,
          }}
        >
          {headersTranslations.sensitiveLabel}
        </FormHelperText>
      </div>
      <div className={classes.fieldRow}>
        <div className={classNames(classes.field, classes.labelWrapper)}>
          <TextBoxWithTooltip variant="subtitle2" text={value.label} />
        </div>
        <div className={classes.checkboxWrapper}>
          <CustomCheckbox
            className={classes.checkbox}
            customRootClass={classes.checkboxRoot}
            customCheckBoxRoot={classNames({
              [classes.disabledCheckboxWrapper]: isVisibleCheckboxDisabled,
            })}
            isChecked={value?.[visibleCheckbox.name]}
            disabled={isVisibleCheckboxDisabled}
            isControlled={visibleCheckbox.isControlled}
            onChange={isChecked =>
              handleChangeCheckbox(isChecked, visibleCheckbox.name)
            }
          />
        </div>
        <div className={classes.checkboxWrapper}>
          <CustomCheckbox
            className={classes.checkbox}
            customRootClass={classes.checkboxRoot}
            customCheckBoxRoot={classNames({
              [classes.disabledCheckboxWrapper]: isSensitiveCheckboxDisabled,
            })}
            isChecked={value?.[sensitiveCheckbox.name]}
            disabled={isSensitiveCheckboxDisabled}
            isControlled={sensitiveCheckbox.isControlled}
            onChange={isChecked =>
              handleChangeCheckbox(isChecked, sensitiveCheckbox.name)
            }
          />
        </div>
      </div>
    </div>
  );
};

PeopleInfoField.propTypes = {
  id: PropTypes.number.isRequired,
  value: PropTypes.object.isRequired,
  headersTranslations: PropTypes.object.isRequired,
  fieldTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PeopleInfoField;
